import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { productEngine } from '@/engines/product-intelligence'
import type { ProductMatchGroup, ProductModel } from '@/engines/product-intelligence'
import { getMockCatalog } from '@/modules/search/utils/mock-catalog'

const QUERY_KEY = ['product-intelligence'] as const

const DEMO_DESCRIPTIONS = [
  'SSD Kingston NV3 1TB NVMe PCIe 4.0',
  'Kingston NV3',
  'Kingston NV3 SSD',
  'SSD Kingston NV3',
  'Kingston SNV3S1000G',
  'Samsung 990 EVO 1TB',
  'WD SN770 1TB NVMe',
  'Notebook Dell Latitude 5450 Intel i5 16GB SSD 512',
  'HP ProBook 450 G10',
  'AMD Ryzen 7 9700X',
  'Intel Core i7-14700K',
  'Kingston Fury Beast DDR5 32GB',
  'Corsair Vengeance DDR5 32GB',
  'GeForce RTX 5060',
  'RTX 5070',
  'HP ProLiant DL380 Gen11',
  'Dell PowerEdge R760',
  'Intelbras AP 1350 AC',
  'Mikrotik hAP ax3',
]

/**
 * useProductIntelligence — UI hook. Uses Search catalog as input without modifying Search Engine.
 */
export function useProductIntelligence() {
  const [query, setQuery] = useState('Kingston NV3')
  const [selected, setSelected] = useState<ProductModel | null>(null)
  const [customInput, setCustomInput] = useState('')

  const catalogQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      productEngine.reset()
      const searchItems = getMockCatalog()
        .slice(0, 80)
        .map((p) => ({
          id: p.id,
          description: p.description,
          sku: p.sku,
          ean: p.ean,
          brand: p.brand,
          model: p.model,
          supplier: p.supplier,
        }))

      const demoEnriched = productEngine.enrichSearchResults(
        DEMO_DESCRIPTIONS.map((description, index) => ({
          id: `demo-${index}`,
          description,
        })),
      )

      const catalogEnriched = productEngine.enrichSearchResults(searchItems)
      const groups = productEngine.matchMany([
        ...DEMO_DESCRIPTIONS,
        ...searchItems.slice(0, 40).map((i) => i.description),
      ])

      return {
        demoEnriched,
        catalogEnriched,
        groups,
        snapshot: productEngine.getCatalog(),
      }
    },
  })

  const filteredGroups = useMemo(() => {
    const groups = catalogQuery.data?.groups ?? []
    const q = query.trim().toLowerCase()
    if (!q) return groups
    return groups.filter((g) => {
      const hay = `${g.product.brand} ${g.product.model} ${g.product.category} ${g.product.fingerprint} ${g.sources.join(' ')}`.toLowerCase()
      return hay.includes(q)
    })
  }, [catalogQuery.data?.groups, query])

  const recognizeCustom = useCallback(() => {
    if (!customInput.trim()) return
    const product = productEngine.recognize(customInput.trim())
    setSelected(product)
  }, [customInput])

  const selectGroup = useCallback((group: ProductMatchGroup) => {
    setSelected(group.product)
  }, [])

  return {
    query,
    setQuery,
    customInput,
    setCustomInput,
    selected,
    setSelected,
    selectGroup,
    recognizeCustom,
    groups: filteredGroups,
    snapshot: catalogQuery.data?.snapshot,
    demoCount: DEMO_DESCRIPTIONS.length,
    isLoading: catalogQuery.isLoading,
    demoDescriptions: DEMO_DESCRIPTIONS,
  }
}
